import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { EventTypeService } from "services/event-type.service";
import { ISaveEventType, IUpdateEventType } from "models/church.types";

export default function EventTypeRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  const [eventType, setEventType] = useState<ISaveEventType | IUpdateEventType>(
    {
      name: "",
    },
  );

  useEffect(() => {
    const initData = async () => {
      if (isEditMode && id) {
        try {
          const data = await EventTypeService.getById(parseInt(id));
          setEventType({
            id: data.id,
            name: data.name,
          });
        } catch (error) {
          console.error("Erreur d'initialisation :", error);
          toast.error("Erreur lors de la récupération du type d'événement");
          history.push("/admin/event-types");
        }
      }
    };
    initData();
  }, [id, isEditMode, history]);

  const validate = (): boolean => {
    const newErrors: { name?: string } = {};
    if (!eventType.name.trim()) {
      newErrors.name = "Le nom du type d'événement est obligatoire";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEventType((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors.hasOwnProperty(name)) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEditMode) {
        await EventTypeService.update(eventType as IUpdateEventType);
        toast.success("Type d'événement mis à jour avec succès");
      } else {
        await EventTypeService.create(eventType as ISaveEventType);
        toast.success("Type d'événement créé avec succès");
      }
      history.push("/admin/event-types");
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      toast.error(
        "Erreur lors de l'enregistrement. Vérifiez si ce type existe déjà.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          {/* Header */}
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-calendar-check mr-2 text-[#D4AF37]"></i>
                {isEditMode
                  ? "Modifier le type d'événement"
                  : "Nouveau Type d'Événement"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Nom du Type */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-tag mr-2"></i>Libellé du type
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={eventType.name}
                    onChange={handleChange}
                    placeholder="Ex: Culte de louange, Réunion de prière, Séminaire..."
                    className={`w-full bg-[#0F172A] border ${
                      errors.name ? "border-red-500" : "border-slate-700"
                    } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-[10px] mt-1 italic">
                      {errors.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-10 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => history.push("/admin/event-types")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
